class ProductListSection {
  constructor(el) {
    this.$el = $(el);
    this.$filterBtns = $();
    this.productSwipers = [];
    // The explore-experiences lists (product-explore-experiences-map.js) render their
    // cards into wrappers owned by this section long after document-ready, and reach
    // back through this expando to have the Swiper rebuilt — see refreshSwiper.
    el.productListSection = this;
    this.init();
  }

  init() {
    this.loadFilters();
    this.loadProductsSwipers();
  }

  loadFilters() {
    this.$filterBtns = this.$el.find('.filter-btn');

    if (!this.$filterBtns.length) return;

    const $activeBtn = this.$filterBtns
      .filter('[aria-selected="true"]')
      .first();

    if ($activeBtn.length) {
      this._setActiveBtn($activeBtn);
      const collectionHandle = $activeBtn.data('collection-handle');
      this._switchCollection(collectionHandle);
    }

    this.$filterBtns.on('click', (e) => {
      const $btn = $(e.currentTarget);
      this._setActiveBtn($btn);
      const collectionHandle = $btn.data('collection-handle');
      this._switchCollection(collectionHandle);
    });
  }

  _setActiveBtn($activeBtn) {
    this.$filterBtns.attr('aria-selected', 'false');
    $activeBtn.attr('aria-selected', 'true');
  }

  /**
   * Fills a collection container that was rendered empty by the section.
   *
   * Only the collection selected on load ships its cards in the document; the rest
   * are fetched from the `card-list` collection view the first time they are opened.
   * Resolves true when the container is ready to be shown.
   */
  async _ensureProducts(container) {
    if (!container) return false;
    if (!container.hasAttribute('data-products-pending')) return true;

    // A second click while the first fetch is in flight must not fire another.
    if (container.dataset.productsLoading === 'true') return false;

    const wrapper = container.querySelector('.swiper-wrapper');
    const handle = container.dataset.collectionHandle;
    if (!wrapper || !handle) return false;

    container.dataset.productsLoading = 'true';

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(
        `${root}collections/${encodeURIComponent(handle)}?view=card-list`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      wrapper.innerHTML = await response.text();
      container.removeAttribute('data-products-pending');
      return true;
    } catch (error) {
      console.warn(`Could not load products for "${handle}":`, error);
      return false;
    } finally {
      delete container.dataset.productsLoading;
    }
  }

  async _switchCollection(collectionHandle) {
    // Scoped to containers: the filter buttons carry the same data attribute, and
    // an unscoped lookup matched those too.
    const $activeContainer = this.$el.find(
      `[data-products-container][data-collection-handle="${collectionHandle}"]`,
    );
    const container = $activeContainer[0];
    if (!container) return;

    // Fetched before anything is hidden, so a slow or failed request leaves the
    // collection already on screen in place rather than swapping in an empty row.
    const ready = await this._ensureProducts(container);
    if (!ready) return;

    this.$el.find('[data-products-container]').addClass('hidden');
    $activeContainer.removeClass('hidden');
    this._showPillTitle(collectionHandle);

    const swiperEl = $activeContainer.find('[data-product-list-swiper]')[0];
    if (swiperEl) {
      // Containers filled on demand have no Swiper yet. Built here rather than at
      // init so it measures an element that is already visible — Swiper reads zero
      // width from a `display: none` ancestor and lays the slides out wrongly.
      const swiper =
        this.productSwipers.find((s) => s.el === swiperEl) ||
        this._createSwiper(swiperEl);

      if (swiper) {
        swiper.update();
        this._updatePrevNav(swiper);
      }
    }
  }

  // Runs only once the row is on screen, so a failed fetch keeps the title matching the cards.
  _showPillTitle(collectionHandle) {
    const $target = this.$el.find('[data-pill-title-target]');
    if (!$target.length) return;

    const title = this.$filterBtns
      .filter(`[data-collection-handle="${collectionHandle}"]`)
      .attr('data-pill-title');
    if (title) $target.text(title);
  }

  /**
   * A virtual row keeps only a window of cards in the page and is as tall as the tallest one
   * mounted, so a taller card arriving mid-swipe made the row, and everything under it, jump.
   * Gift cards differ by a line wherever a title or its line of examples takes one line or two
   * (snippets/product-card.liquid reserves no empty line under a short one). So every card is
   * measured once, as a copy at the slide's width, off-screen and with its images stripped so
   * nothing is fetched, and the row is held at the tallest. Again when the width changes (a phone
   * turned) and once the web fonts are in.
   */
  _holdRowHeight(swiper) {
    let measuredWidth = 0;

    const measure = (force) => {
      if (swiper.destroyed) return;
      const slides = swiper.virtual?.slides || [];
      const width = swiper.slides[0]?.getBoundingClientRect().width || 0;
      if (!width || !slides.length || (!force && width === measuredWidth))
        return;
      measuredWidth = width;

      const probe = document.createElement('div');
      probe.style.cssText = `position: absolute; top: 0; left: -10000px; width: ${width}px; visibility: hidden;`;
      slides.forEach((slide) => {
        const copy = slide.cloneNode(true);
        // Swiper's inline width and offset; the probe sets the width.
        copy.removeAttribute('style');
        copy.querySelectorAll('img').forEach((img) => {
          img.removeAttribute('srcset');
          img.removeAttribute('src');
        });
        probe.appendChild(copy);
      });
      swiper.el.appendChild(probe);
      const tallest = Math.max(
        ...Array.from(
          probe.children,
          (copy) => copy.getBoundingClientRect().height,
        ),
      );
      probe.remove();
      swiper.wrapperEl.style.minHeight = `${Math.ceil(tallest)}px`;
    };

    measure(true);
    swiper.on('resize', () => measure(false));
    swiper.on('update', () => measure(false));
    document.fonts?.ready.then(() => measure(true));
  }

  _updatePrevNav(swiper) {
    ProductListSection.updatePrevNav(swiper);
  }

  static updatePrevNav(swiper) {
    /* A list is allowed to ship no back arrow at all — the explore-experiences carousel does,
     * because on a phone "back" is what swiping is for and a 48px circle sitting on the card
     * only competes with the tap that opens it.
     *
     * The guard has to count, not test truthiness. Swiper resolves an unmatched navigation
     * selector to an *empty array* (`makeElementsArray` on `[...querySelectorAll()]`), and `[]`
     * is truthy — so a plain `if (!prev) return` sails past it and `[].classList.toggle` throws
     * inside init, slideChange and resize. `prevEl` is likewise an element when one matches and
     * an array when several do, so both shapes are normalised here. */
    const prevEls = [swiper?.navigation?.prevEl].flat().filter(Boolean);
    if (prevEls.length === 0) return;

    const isDesktop = window.matchMedia('(min-width: 750px)').matches;
    const isMobileOnlySwiper = swiper.el?.className.includes('md:hidden');
    const shouldShowPrev = isMobileOnlySwiper ? !isDesktop : isDesktop;
    const hide = !shouldShowPrev || swiper.isBeginning;

    prevEls.forEach((el) => el.classList.toggle('hidden', hide));
  }

  /**
   * Called by the explore-experiences lists after they (re)render a wrapper whose
   * Swiper was created empty at document-ready. A plain update() is all desktop
   * needs, but on touch devices a freshly filled 20-30-card strip is exactly the
   * pinch-zoom killer _createSwiper virtualizes against — and an already-virtual
   * instance has to be rebuilt regardless, because the re-render replaced the
   * elements its slide window was drawn from.
   */
  refreshSwiper(swiperEl) {
    const existing = swiperEl ? swiperEl.swiper : null;
    if (!existing) return null;

    // On an already-virtual instance the wrapper holds only the mounted window
    // (~4-7 slides); the full set lives in virtual.slides. Some callers reach
    // here WITHOUT a re-render (the map/list toggle), so the wrapper cannot be
    // trusted as the slide source: rebuilding from it would silently truncate a
    // 30-card strip to the window. Both list renders replace the wrapper with
    // freshly cloned elements, so "every child is already a virtual slide"
    // reliably means no re-render happened — restore the full set before
    // _createSwiper harvests the wrapper.
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    const mounted = wrapper ? Array.from(wrapper.children) : [];
    const virtualSlides = existing.params.virtual?.enabled
      ? existing.virtual?.slides || []
      : [];
    const isStaleWindow =
      virtualSlides.length > 0 &&
      mounted.every((el) => virtualSlides.includes(el));
    const slideEls = isStaleWindow ? virtualSlides : mounted;
    const wantsVirtual =
      slideEls.length >= 6 && window.matchMedia('(pointer: coarse)').matches;

    if (!wantsVirtual && !existing.params.virtual?.enabled) {
      existing.update();
      return existing;
    }

    const previousIndex = existing.activeIndex || 0;
    const listIndex = this.productSwipers.indexOf(existing);
    existing.destroy(true, true);
    if (listIndex >= 0) this.productSwipers.splice(listIndex, 1);
    if (isStaleWindow && wrapper) wrapper.replaceChildren(...slideEls);

    const swiper = this._createSwiper(swiperEl);
    // A Load More re-render must not yank the shopper back to the first card.
    if (swiper && previousIndex > 0) swiper.slideTo(previousIndex, 0);
    return swiper;
  }

  /**
   * Grow a virtual carousel in place — the smooth half of `refreshSwiper`.
   *
   * A load more only ever adds to the end, and a rebuild is a very expensive way to say that:
   * `destroy(true, true)` strips the wrapper's transform and every slide's width, `_createSwiper`
   * detaches all of them again, and the cards under the thumb get torn out of the document and
   * put back. They keep their decoded images now, but the browser still repaints them from
   * scratch — which is the flicker that survived making the render additive.
   *
   * Swiper's own `virtual.appendSlide` is no better: it ends in `update(true)`, and the forced
   * path opens with `slides.forEach(el => el.remove())` — every mounted slide out of the DOM and
   * back in. So this pushes onto `virtual.slides` directly and asks for an *unforced* update.
   * That path only removes slides that fell out of the window and appends ones that came into
   * it; when the shopper is mid-list and the window has not moved, it touches no DOM at all.
   *
   * `updateSlides` still has to run: it is what rebuilds `snapGrid`/`slidesGrid`, and with
   * virtual slides it sizes the whole list rather than the mounted window, so without it the
   * carousel would refuse to swipe past the cards it already had. It writes the same width and
   * margin onto the mounted slides it wrote before, so it is not itself visible.
   *
   * Deliberately no `slideTo`: nothing here changes the wrapper's translate, so the card under
   * the thumb stays exactly where it is. `swiper.update()` is what must be avoided — it ends in
   * `slideTo(slides.length - 1)` whenever `isEnd`, and `reachEnd` is one of the two events that
   * triggers a load more, so it would fling the shopper to the last of the new cards.
   *
   * @returns {boolean} False when this carousel cannot be grown in place and needs the rebuild.
   */
  appendSlides(swiperEl, added) {
    const swiper = swiperEl?.swiper;
    if (!swiper?.params?.virtual?.enabled || !swiper.virtual) return false;
    if (added.length === 0) return true;

    swiper.virtual.slides.push(...added);
    swiper.updateSlides();
    swiper.virtual.update(false);
    swiper.updateSlidesClasses();
    this._updatePrevNav(swiper);

    return true;
  }

  static refreshSwiperNav(wrapper) {
    if (!wrapper) return;

    const swiperEl = wrapper.closest('.product-list-swiper');
    const swiper = swiperEl?.swiper;
    if (!swiper) return;

    swiper.update();
    ProductListSection.updatePrevNav(swiper);
  }

  loadProductsSwipers() {
    const swiperEls = this.$el.find('[data-product-list-swiper]');

    if (!swiperEls.length || typeof window.Swiper === 'undefined') return;

    // Destroy existing swipers
    this.productSwipers.forEach((swiper) => {
      if (swiper) swiper.destroy(true, true);
    });
    this.productSwipers = [];

    // Initialize a swiper for each collection whose cards are already in the
    // document. The rest get theirs in _switchCollection, once fetched — a Swiper
    // built over an empty wrapper has no slides to lay out.
    swiperEls.each((index, swiperEl) => {
      const container = $(swiperEl).closest('[data-products-container]')[0];
      if (container?.hasAttribute('data-products-pending')) return;

      this._createSwiper(swiperEl);
    });
  }

  _createSwiper(swiperEl) {
    if (typeof window.Swiper === 'undefined') return null;

    const swiperId = swiperEl.dataset.productListSwiper;
    const section = this;

    // On touch devices a fully-populated strip (13 cards ≈ 4,600px on the homepage
    // lists) is what jetsams the tab under pinch zoom: WebKit backs the carousel —
    // translated wrapper and native scroller alike — with GPU memory that scales
    // with strip size x zoom² x dpr², so a page holding a few of these dies at
    // ~4-5x zoom ("Aw, Snap!" after a silent reload; isolated on-device 2026-08-31,
    // the same pinch survived with the strips display:none). Virtual slides keep
    // only the active window of cards mounted (~6 on mobile), which is also the
    // Swiper maintainer's prescribed fix for its many-slides mobile crashes
    // (nolimits4web/swiper#3144, #3264). The original card *elements* are handed
    // back from renderSlide — never serialized — so any bound listeners survive.
    // Confined to coarse pointers: desktop keeps today's behaviour, and Swiper 11's
    // known virtual+resize quirk (fixed upstream in 12.1.3) stays off mouse-driven
    // windows that actually get resized. Skipped for wrappers that are empty at
    // init (the explore lists fill theirs later by appending into the DOM, which
    // virtual mode would wipe on its next render).
    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    const slideEls = wrapper ? Array.from(wrapper.children) : [];
    let virtualParams = null;

    if (
      slideEls.length >= 6 &&
      window.matchMedia('(pointer: coarse)').matches
    ) {
      wrapper.replaceChildren();
      virtualParams = {
        slides: slideEls,
        renderSlide: (slideEl) => slideEl,
        addSlidesBefore: 2,
        addSlidesAfter: 2,
      };
    }

    const swiper = new window.Swiper(swiperEl, {
      ...(virtualParams ? { virtual: virtualParams } : {}),
      // 295px card with a 48px peek of the next one at the 375px design width:
      // solving (343 - 16 * (spv - 1)) / spv = 295 gives spv = 359 / 311.
      slidesPerView: 359 / 311,
      slidesPerGroup: 1,
      spaceBetween: 16,
      // The default ('wrapper') drops any swipe whose hit target is not
      // inside the wrapper element. The wrapper's own box is only one
      // container wide and translates with the carousel, so once the user
      // has swiped past the first group, the visible spaceBetween gaps sit
      // outside it and hit-test to the container — a finger landing on a gap
      // then swipes nothing. The listeners live on the container anyway;
      // this just stops those touches being filtered out.
      touchEventsTarget: 'container',
      speed: 300,
      watchOverflow: true,
      grid: {
        rows: 1,
        fill: 'row',
      },
      navigation: {
        nextEl: `.swiper-nav-next-${swiperId}`,
        prevEl: `.swiper-nav-prev-${swiperId}`,
      },
      // Two cards from the theme's `md` (750px) and three from `lg` (990px): three across a
      // 750-989px row made each card about 185px wide, too narrow for a title, its stars and a
      // price with its saving. `.product-list-swiper` in assets/app-tailwind.css lays the first
      // row out with these same cards and gaps until this runs: change the two together.
      breakpoints: {
        750: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 24,
          grid: {
            rows: 1,
          },
        },
        990: {
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 24,
          grid: {
            rows: 1,
          },
        },
        1280: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 24,
          grid: {
            rows: 1,
          },
        },
      },
      on: {
        init(swiperInstance) {
          section._updatePrevNav(swiperInstance);
        },
        slideChange(swiperInstance) {
          section._updatePrevNav(swiperInstance);
        },
        resize(swiperInstance) {
          section._updatePrevNav(swiperInstance);
        },
      },
    });

    this.productSwipers.push(swiper);
    if (virtualParams) this._holdRowHeight(swiper);

    // The explore-experiences lists bind their swipe-to-load-more to the Swiper
    // instance itself (bindCarouselAutoLoad in
    // assets/product-explore-experiences-map.js), and their first render can beat
    // this call: the Swipers are built from jQuery's ready callback, which fires
    // asynchronously, while a prefetched /experiences response resolves in a
    // microtask straight after DOMContentLoaded. Announce the instance so a list
    // that found none can bind to it now.
    swiperEl.dispatchEvent(
      new CustomEvent('everwow:swiper:created', { bubbles: true }),
    );

    return swiper;
  }
}

class ImagePreviewModal {
  constructor() {
    this.dialog = null;
    this.img = null;
    this.init();
  }

  init() {
    this._createModal();
    this._bindEvents();
  }

  // A native modal <dialog>, laid out like snippets/experience-details-modal.liquid:
  // it fills the viewport transparently and centres the card, so a click anywhere
  // around the card lands on the dialog itself. Styles: `.image-preview-modal`.
  _createModal() {
    this.dialog = document.createElement('dialog');
    this.dialog.className =
      'image-preview-modal motion-dialog motion-dialog--box';
    // Focusable, so opening can put focus on the dialog itself (see the click handler).
    this.dialog.tabIndex = -1;
    this.dialog.innerHTML = `
      <div class="image-preview-modal__inner">
        <button type="button" class="image-preview-modal__close" aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="var(--shop-color-neutral-8)"/>
          </svg>
        </button>
        <img class="image-preview-modal__img" alt="preview-image" />
      </div>
    `;
    this.img = this.dialog.querySelector('.image-preview-modal__img');
    document.body.append(this.dialog);
  }

  _bindEvents() {
    $(document).on('click', '.preview_image', (e) => {
      const $button = $(e.currentTarget);
      const imageUrl = $button.data('image');
      if (!imageUrl) return;
      // The image is sized from its ratio (`.image-preview-modal__img`), so it takes
      // its final size before the file has loaded. Without one it falls back to the
      // file's own size.
      const ratio = $button.data('image-ratio');
      if (ratio) this.img.style.setProperty('--preview-image-ratio', ratio);
      else this.img.style.removeProperty('--preview-image-ratio');
      this.img.src = imageUrl;
      this.dialog.showModal();
      // showModal() focuses the first focusable element, the close button, and the
      // browser rings it even on a tap. Take focus onto the dialog instead: Escape
      // still closes it, and Tab still reaches the button with its ring.
      this.dialog.focus();
    });

    // Anything inside the card is the card's own, bar the close button; a click
    // on the backdrop around it closes the dialog.
    this.dialog.addEventListener('click', (e) => {
      if (
        e.target.closest('.image-preview-modal__close') ||
        !e.target.closest('.image-preview-modal__inner')
      ) {
        this.close();
      }
    });

    // Escape closes a modal <dialog> natively. `close` fires for that and for
    // close() alike, so the image is dropped in one place.
    this.dialog.addEventListener('close', () => {
      this.img.removeAttribute('src');
    });
  }

  close() {
    if (this.dialog.open) this.dialog.close();
  }
}
class Search {
  constructor() {
    // Component containers
    this.$desktopComponent = $('[data-search-dropdown]');
    this.$mobileComponent = $('[data-mobile-search-modal]');

    // Unique desktop elements
    this.$desktopInput = $('[data-search-input]');
    this.$desktopForm = $('[data-search-form]');
    this.$desktopTrigger = $('[data-desktop-search-trigger]');

    // Unique mobile elements
    this.$mobileInput = $('[data-mobile-search-input]');
    this.$mobileForm = $('[data-mobile-search-form]');
    this.$mobileToggle = $('[data-mobile-search-toggle]');
    this.$mobileClose = $('[data-mobile-search-close]');

    this.clearButtonSyncs = {};

    this.init();
  }

  _panel(context) {
    const $component =
      context === 'desktop' ? this.$desktopComponent : this.$mobileComponent;
    return $component.find('[data-search-guided]')[0] || null;
  }

  init() {
    if (!this.$desktopComponent.length && !this.$mobileComponent.length) return;

    // The panel (assets/search-guided.js) renders the quiz rows, the gift finder pick and the
    // results; this class keeps the field, opening, closing and focus.
    ['desktop', 'mobile'].forEach((context) => {
      const panel = this._panel(context);
      const $component =
        context === 'desktop' ? this.$desktopComponent : this.$mobileComponent;
      const input =
        context === 'desktop' ? this.$desktopInput[0] : this.$mobileInput[0];
      if (panel && input && window.SearchGuided) {
        window.SearchGuided.mount(
          panel,
          input,
          context,
          $component.find('[data-search-scroll]')[0],
        );
      }
    });

    this._bindEvents();
  }

  _render(context, value) {
    const panel = this._panel(context);
    if (panel && window.SearchGuided) window.SearchGuided.show(panel, value);
  }

  _bindEvents() {
    // Warm the price table and the panel's data the moment the shopper shows intent, so both are
    // ready by the time the panel paints.
    const warm = () => {
      window.SearchEngine?.warm();
      window.SearchGuided?.preload();
    };
    this.$desktopInput.on('focus', warm);
    this.$mobileToggle.on('click', warm);

    // Desktop: show dropdown on focus
    this.$desktopInput.on('focus', () => {
      this._open('desktop');
      this._render('desktop', this.$desktopInput.val());
    });

    // Desktop: toggle dropdown on search icon click
    this.$desktopTrigger.on('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Toggle: close if open, open if closed
      if (!this.$desktopComponent.hasClass('hidden')) {
        this._close('desktop');
      } else {
        this._open('desktop');
        this._render('desktop', this.$desktopInput.val());
        this.$desktopInput.focus();
      }
    });

    // Desktop: handle input
    this.$desktopInput.on('input', (e) => {
      this._open('desktop');
      this._render('desktop', e.target.value);
    });

    // Desktop: clicking the input reopens the dropdown even when it already has focus
    // (it may have been closed by a mega menu opening on hover).
    this.$desktopInput.on('click', (e) => {
      e.stopPropagation();
      this._open('desktop');
      this._render('desktop', this.$desktopInput.val());
    });
    this.$desktopComponent.on('click', (e) => e.stopPropagation());

    // Desktop: close on outside click
    $(document).on('click', (e) => {
      if (
        !this.$desktopComponent[0].contains(e.target) &&
        !$('.header__search-wrapper')[0]?.contains(e.target)
      ) {
        this._close('desktop');
      }
    });

    // Desktop: close on escape
    this.$desktopInput.on('keydown', (e) => {
      if (e.key === 'Escape') this._close('desktop');
    });

    // Desktop: form submit
    this.$desktopForm.on('submit', () => {
      window.SearchGuided?.saveRecent(this.$desktopInput.val());
    });

    // Mobile: open modal
    this.$mobileToggle.on('click', () => {
      // `_open()` has to come first: it un-hides the modal (an element still on
      // `display: none` cannot take focus) and closes the mobile menu, which focuses the
      // hamburger on its way out — focusing before that would hand focus straight back.
      this._open('mobile');
      this._focusMobileInput();

      // Everything below is safe after the focus: none of it touches the header form, so
      // none of it can blur the input.
      this.$mobileToggle.attr('aria-expanded', 'true');
      this.$mobileInput.val('');
      this._syncClearButton('mobile');
      this._render('mobile', '');
      $('body').css('overflow', 'hidden');
    });

    // Mobile: close modal
    this.$mobileClose.on('click', () => this._closeMobileSearch());

    // Mobile: handle input
    this.$mobileInput.on('input', (e) => {
      this._render('mobile', e.target.value);
    });

    // Mobile: form submit
    this.$mobileForm.on('submit', () => {
      window.SearchGuided?.saveRecent(this.$mobileInput.val());
    });

    // Mobile: close on escape
    $(document).on('keydown', (e) => {
      if (e.key === 'Escape') this._closeMobileSearch();
    });

    // Both: clear button visibility follows the input value
    this._bindClearButton('desktop');
    this._bindClearButton('mobile');
  }

  _syncClearButton(context) {
    this.clearButtonSyncs[context]?.();
  }

  _bindClearButton(context) {
    const $input =
      context === 'desktop' ? this.$desktopInput : this.$mobileInput;
    const $clear = $input.closest('form').find('[data-search-clear]');

    if (!$input.length || !$clear.length) return;

    const sync = () => $clear.toggleClass('hidden', !$input.val().length);

    // Kept so the handlers that empty the input with `.val('')` can re-sync — assigning a
    // value in JS fires no `input` event, which would otherwise strand a visible clear
    // button over an empty field (open mobile search, type, close, reopen).
    this.clearButtonSyncs[context] = sync;

    $input.on('input', sync);

    $clear.on('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      $input.val('').focus();
      sync();
      this._render(context, '');
    });

    sync();
  }

  _open(context) {
    // Search, the mega menu and the mobile menu are mutually exclusive — only one
    // header panel may be open at a time, otherwise they overlap.
    if (window.headerController) {
      if (window.headerController.isMobileMenuOpen?.()) {
        window.headerController.closeMobileMenu();
      }

      window.headerController.closeDesktopMenus();
    }

    const $component =
      context === 'desktop' ? this.$desktopComponent : this.$mobileComponent;
    if ($component.hasClass('hidden')) {
      const panel = this._panel(context);
      if (panel) window.SearchGuided?.opened(panel, context);
    }

    if (context === 'desktop') {
      // The design dims the page behind the desktop dropdown exactly like the mega menu does.
      // Set after closeDesktopMenus() above, which clears the overlay on its way out.
      window.headerController?.toggleOverlay(true);
      return this.$desktopComponent.removeClass('hidden');
    }
    this.$mobileComponent.removeClass('hidden');
  }

  _close(context) {
    const $component =
      context === 'desktop' ? this.$desktopComponent : this.$mobileComponent;
    $component.addClass('hidden');

    // header.js hides the search straight through here when the hamburger
    // opens the menu, so the toggle state and the scroll lock must be released
    // here — not only in _closeMobileSearch(), whose hidden-guard would then
    // skip them forever.
    if (context === 'mobile') {
      this.$mobileToggle.attr('aria-expanded', 'false');
      $('body').css('overflow', '');
    }

    // Only drop the overlay if a mega menu panel isn't holding it open.
    if (context === 'desktop' && !window.headerController?.activeDesktopItem) {
      window.headerController?.toggleOverlay(false);
    }
  }

  // iOS Safari ties the software keyboard to user activation: the `focus()` that raises it
  // has to run synchronously inside the gesture handler. Deferring it — this was a
  // `setTimeout(…, 100)` — leaves the modal open with a focused field, no caret and no
  // keyboard, so the user has to tap the input a second time before they can type.
  //
  // The rAF pass only covers the case where something reclaims focus later in the same tick;
  // it is deliberately not the primary path, because by the time it runs the activation is
  // spent and iOS would give focus without the keyboard.
  _focusMobileInput() {
    const input = this.$mobileInput[0];
    if (!input) return;

    // `preventScroll` so iOS doesn't scroll the page underneath the fixed overlay.
    input.focus({ preventScroll: true });

    requestAnimationFrame(() => {
      if (document.activeElement !== input) {
        input.focus({ preventScroll: true });
      }
    });
  }

  _closeMobileSearch() {
    if (this.$mobileComponent.hasClass('hidden')) return;

    this._close('mobile');
    // Still blurred explicitly: on desktop the toggle is `md:hidden`, so the focus() below
    // is a no-op and the input would otherwise keep focus.
    this.$mobileInput.val('').blur();
    this._syncClearButton('mobile');

    this.$mobileToggle[0]?.focus({ preventScroll: true });
  }
}

/* ================================================================
   BackButton — sends [data-back-button] links to the previously visited
   page instead of their href.

   The href is left intact and is what actually runs whenever there is
   nothing safe to go back to: a direct landing, a new tab, an external
   referrer, or a reload (browsers drop the referrer). Only a same-origin
   referrer counts, so "back" never bounces the buyer off the store.
================================================================ */
class BackButton {
  constructor() {
    $(document).on('click', '[data-back-button]', (e) => {
      if (!BackButton.canGoBack()) return; // fall through to the href
      e.preventDefault();
      window.history.back();
    });
  }

  static canGoBack() {
    if (window.history.length <= 1) return false;
    if (!document.referrer) return false;
    try {
      return new URL(document.referrer).origin === window.location.origin;
    } catch (err) {
      return false;
    }
  }
}

class HeaderCartCount {
  constructor() {
    this.init();
  }

  init() {
    this.cartLink = document.querySelector('.header__cart');
    this.badge = this.cartLink?.querySelector('.header__cart-count');
    this.labelTemplate = this.cartLink?.dataset.cartItemsLabel;
    if (!this.cartLink || !this.badge) return;

    this.bindEvents();
    this.refresh();
  }

  bindEvents() {
    document.addEventListener('product:added-to-cart', () => this.refresh());
    // The cart page changes the cart without leaving it (assets/cart.js).
    document.addEventListener('cart:changed', () => this.refresh());
    // `persisted` only. `pageshow` fires on every load, not just bfcache restores,
    // so this ran a second identical /cart.js immediately after the one in init()
    // — two requests on every page of the site, the badge painted twice with the
    // same number, and on the product page they competed with the add-to-cart read
    // for the same connection. A fresh load is already covered by init(); a
    // bfcache restore is the case that genuinely needs re-reading, because the cart
    // may have changed on the page the shopper is coming back from.
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) this.refresh();
    });
  }

  async refresh() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) return;

      // Gifts, not lines: every experience or gift card is one gift, and a gift
      // box is part of its gift. Mirrors the count in snippets/header-top.liquid.
      const cart = await response.json();
      const { items } = cart;

      // Shared with the email pop-up (TN-17), which needs the cart's codes and gifts on
      // every page it runs on: one /cart.js read instead of two competing ones.
      window.EwCartSnapshot = cart;
      document.dispatchEvent(
        new CustomEvent('ew:cart-snapshot', { detail: cart }),
      );
      const count = items.filter((item) =>
        ['experience-gift', 'gift-card'].includes(
          (item.product_type || '').toLowerCase(),
        ),
      ).length;
      this.badge.textContent = count;
      this.badge.classList.toggle('hidden', count <= 0);

      if (this.labelTemplate)
        this.cartLink.setAttribute(
          'aria-label',
          this.labelTemplate.replace('__COUNT__', String(count)),
        );
    } catch (error) {
      // Badge stays at server-rendered value if cart fetch fails.
    }
  }
}

class Theme {
  constructor() {
    this.loadThemeSections();
    this.imagePreviewModal = new ImagePreviewModal();
    this.search = new Search();
    this.backButton = new BackButton();
    this.headerCartCount = new HeaderCartCount();

    // Store search instance globally for cross-component communication
    window.searchInstance = this.search;
  }

  loadThemeSections() {
    this.loadProductListSection();
  }

  loadProductListSection() {
    $('[data-section-type="product-list"]').each(function () {
      new ProductListSection(this);
    });
  }
}

$(document).ready(() => new Theme());

// Since the click below opens the picker, the field never needs mouse focus — and
// without focus Chrome stops painting the system highlight on whichever dd/mm/yyyy
// segment sits under the cursor, which otherwise reads as a stray blue block. No
// CSS reaches that highlight (::-webkit-datetime-edit-*-field:focus is ignored,
// even with !important), so suppressing the focus is the only lever. This affects
// the mouse only: Tab still focuses the field and highlights segments as usual.
//
// Pointer-fine only. On touch the mousedown is synthesised from the tap, and
// preventing its default cancels the rest of that synthesised chain — including
// the click the handler below listens for — so the field opened its picker only
// where a tap reached the native segments and read as dead everywhere else.
$(document).on('mousedown', 'input[type="date"]', function (e) {
  if (this.disabled || this.readOnly || typeof this.showPicker !== 'function') {
    return;
  }
  if (!window.matchMedia('(pointer: fine)').matches) {
    return;
  }
  e.preventDefault();
});

// A native date input only opens its picker from the calendar icon — clicking the
// text reads as a dead field. Open it from anywhere in the control. Delegated, so
// it also covers date inputs added after load (e.g. the cart's schedule fields).
$(document).on('click', 'input[type="date"]', function () {
  if (this.disabled || this.readOnly || typeof this.showPicker !== 'function') {
    return;
  }
  try {
    this.showPicker();
  } catch (e) {
    // showPicker() throws when the browser does not consider this a user
    // gesture; the field still works as a plain typed input in that case.
  }
});

// A .date-field shows a "Select date" placeholder while its input is empty (the CSS
// lives in assets/app.scss — a date input can't take a real `placeholder`). Liquid
// renders the initial flag so it survives a section re-render; this only has to track
// it as the value changes. Delegated, so it covers date inputs added after load.
$(document).on('change input', 'input[type="date"]', function () {
  const field = this.closest('.date-field');
  if (field) {
    field.classList.toggle('date-field--empty', !this.value);
  }
});

// MutationObserver: Watch for DOM changes (hot reload adds sections to DOM)
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    if (mutation.addedNodes.length) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          const $node = $(node);
          let $productList = $node.find('[data-section-type="product-list"]');

          if (
            !$productList.length &&
            $node.attr('data-section-type') === 'product-list'
          ) {
            $productList = $node;
          }

          if ($productList.length) {
            setTimeout(() => {
              new ProductListSection($productList[0]);
            }, 250);
          }
        }
      });
    }
  });
});

if (document.body) {
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
}

// Reviews blocks show ONE review, chosen at random on load, rather than a carousel the
// shopper has to page through. The product page has worked this way for a while; the cart
// block used to be a Swiper with prev/next arrows and an "n of m" counter, which asked the
// shopper to do work at the exact moment the page is trying to get out of their way — and
// cost a Swiper instance, a font-metrics measuring pass and two arrow buttons for it.
//
// Lives here rather than in product.js or cart.js because it is now the behaviour of every
// reviews block on the site and the two files never load together. The markup contract is
// `[data-random-review]` on the block, `[data-review-items]` on the list inside it. The list
// ships `hidden` so the full set never flashes before one is picked, and `data-random-review-
// ready` makes a second call a no-op.
(function randomReviewBlocks() {
  const init = () => {
    document.querySelectorAll('[data-random-review]').forEach((root) => {
      if (root.dataset.randomReviewReady === 'true') return;

      const items = root.querySelector('[data-review-items]');
      if (!items) return;

      const children = Array.from(items.children);
      if (children.length > 1) {
        const keep = Math.floor(Math.random() * children.length);
        children.forEach((child, index) => {
          if (index !== keep) child.remove();
        });
      }

      items.classList.remove('hidden');
      root.dataset.randomReviewReady = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

// Store policy pages (/policies/*) are rendered by Shopify, not by a theme template,
// and its policy editor strips markup added to the content itself, so the scroll
// container for wide tables has to be created here. Progressive enhancement: without
// it the tables still fit the page, they just wrap instead of scrolling. Styles are
// under `.policy-table` in app.scss.
(function wrapPolicyTables() {
  const init = () => {
    document
      .querySelectorAll('.shopify-policy__body table')
      .forEach((table) => {
        if (table.parentElement.classList.contains('policy-table')) return;

        const wrapper = document.createElement('div');
        wrapper.className = 'policy-table';
        table.parentNode.insertBefore(wrapper, table);
        wrapper.appendChild(table);
      });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
